import { POSITIONS_MARKET_ACCOUNTS_TAB_TEST_ID, TourName } from '@src/tokens/tours';
import {
  AccordionGroup,
  BLOTTER_TABLE_FILTERS_CONTAINER_ID,
  Box,
  EmptyView,
  HStack,
  MixpanelEvent,
  MixpanelEventProperty,
  Tab,
  TabAppearance,
  TabList,
  TabPanels,
  TabSize,
  Tabs,
  Tour,
  tabLabelerByType,
  useAddTabPopover,
  useCallbackRef,
  useDynamicCallback,
  useMixpanel,
  usePersistedTabs,
  usePortal,
  useTabs,
  type UseTabsProps,
} from '@talos/kyoko';
import { useCallback, useMemo, useState } from 'react';
import { useBlotterTableTabCloning } from '../../../providers/AppConfigRedux/useBlotterTableTabCloning';
import { PositionsDerivativesBlotter } from './Derivatives/PositionsDerivativesBlotter';
import { PositionsPredictionsBlotter } from './Predictions/PositionsPredictionsBlotter';
import { PositionsSubAccountsBlotter } from './SubAccounts/PositionsSubAccountsBlotter';
import { PositionsUnifiedBlotter } from './Unified/PositionsUnifiedBlotter';
import {
  POSITIONS_TABS_EXCLUDED_IDS,
  getDefaultPositionsTabProperties,
  isEnabledPositionsTabType,
  useDefaultPositionsTabs,
  useEnabledPositionsTabTypes,
} from './defaults';
import { getPositionsTabTypeLabel, normalizePersistedPositionsTabLabel } from './positionsTabLabels';
import { POSITIONS_V3_BLOTTER_PREFIX } from './tokens';
import type { PositionsTabProps, PositionsTabType } from './types';

const tabLabeler: UseTabsProps<PositionsTabProps>['tabLabeler'] = tabLabelerByType<PositionsTabProps, 'tabType'>({
  typeKey: 'tabType',
  typeToLabel: getPositionsTabTypeLabel,
});

function getBlotterID(tabID: string | undefined) {
  return `${POSITIONS_V3_BLOTTER_PREFIX}/${tabID}`;
}

function renderPositionsTab(
  tab: PositionsTabProps,
  {
    initialFiltersOpen,
    onUpdateTab,
    onCloneTab,
  }: {
    initialFiltersOpen: boolean;
    onUpdateTab: (tab: PositionsTabProps) => void;
    onCloneTab: () => void;
  }
) {
  const blotterID = getBlotterID(tab.id);
  switch (tab.tabType) {
    case 'Derivatives':
      return (
        <PositionsDerivativesBlotter
          blotterID={blotterID}
          tab={tab}
          onUpdateTab={onUpdateTab}
          onCloneTab={onCloneTab}
          initialIsOpen={initialFiltersOpen}
        />
      );
    case 'Sub Accounts':
      return (
        <PositionsSubAccountsBlotter
          blotterID={blotterID}
          tab={tab}
          onUpdateTab={onUpdateTab}
          onCloneTab={onCloneTab}
          initialIsOpen={initialFiltersOpen}
        />
      );
    case 'Unified':
      return (
        <PositionsUnifiedBlotter
          blotterID={blotterID}
          tab={tab}
          onUpdateTab={onUpdateTab}
          onCloneTab={onCloneTab}
          initialIsOpen={initialFiltersOpen}
        />
      );
    case 'Predictions':
      return (
        <PositionsPredictionsBlotter
          blotterID={blotterID}
          tab={tab}
          onUpdateTab={onUpdateTab}
          onCloneTab={onCloneTab}
          initialIsOpen={initialFiltersOpen}
        />
      );
    default: {
      const _exhaustive: never = tab;
      return _exhaustive;
    }
  }
}

export function PositionsV3() {
  const mixpanel = useMixpanel();

  const [initialFiltersOpen, setInitialFiltersOpen] = useState(false);

  const handleSelect = useCallback(() => setInitialFiltersOpen(false), []);
  const handleAdd = useCallback(() => setInitialFiltersOpen(true), []);

  const tabTypeOptions = useEnabledPositionsTabTypes();
  const defaultPositionsTabs = useDefaultPositionsTabs();

  const persistedTabs = usePersistedTabs<PositionsTabProps>(POSITIONS_V3_BLOTTER_PREFIX, {
    defaultInitialItems: defaultPositionsTabs,
    defaultInitialSelectedIndex: 0,
    excludedIDs: POSITIONS_TABS_EXCLUDED_IDS,
    onSelect: handleSelect,
  });

  const filteredInitialItems = useMemo(() => {
    return persistedTabs.initialItems
      .filter(tab => isEnabledPositionsTabType(tab.tabType, tabTypeOptions))
      .map(normalizePersistedPositionsTabLabel);
  }, [persistedTabs.initialItems, tabTypeOptions]);

  const initialSelectedIndex = useMemo(() => {
    const selectedTab = persistedTabs.initialItems[persistedTabs.initialSelectedIndex];
    if (!selectedTab || !isEnabledPositionsTabType(selectedTab.tabType, tabTypeOptions)) {
      return 0;
    }
    const indexInFiltered = filteredInitialItems.findIndex(t => t.id === selectedTab.id);
    return indexInFiltered >= 0 ? indexInFiltered : 0;
  }, [persistedTabs.initialItems, persistedTabs.initialSelectedIndex, filteredInitialItems, tabTypeOptions]);

  // `useTabs` re-fires when the onSelect callback identity changes, so this wrapper must stay stable
  // while still seeing the latest mixpanel instance and persisted tab handlers.
  const onTabSelected = useCallbackRef<NonNullable<UseTabsProps<PositionsTabProps>['onSelect']>>(
    (index: number, tabs: PositionsTabProps[]) => {
      mixpanel.track(MixpanelEvent.SetPositionBlotterTab, {
        [MixpanelEventProperty.TabIndex]: index,
        [MixpanelEventProperty.TabLabel]: tabs?.[index]?.label ?? '',
        [MixpanelEventProperty.TabType]: tabs?.[index]?.tabType ?? 'unknown',
      });
      persistedTabs.onSelect(index, tabs);
    }
  ) satisfies UseTabsProps['onSelect'];

  const tabs = useTabs<PositionsTabProps>({
    ...persistedTabs,
    initialItems: filteredInitialItems,
    initialSelectedIndex,
    showAddTab: tabTypeOptions.length > 0,
    tabLabeler,
    onAdd: handleAdd,
    onSelect: onTabSelected,
    onClone: useBlotterTableTabCloning({ getBlotterID }),
  });

  const handleCloneTab = useDynamicCallback(() => {
    tabs.clone(tabs.selectedIndex, {
      closable: true,
      reorderable: true,
    });
  });

  const handleUpdateTab = useDynamicCallback((updatedTab: PositionsTabProps) => {
    tabs.updateItem(updatedTab);
  });

  const handleNewTabTypeSelection = useCallbackRef((tabType: PositionsTabType) => {
    mixpanel.track(MixpanelEvent.AddTab, {
      [MixpanelEventProperty.TabType]: tabType,
    });
    tabs.onAdd?.(getDefaultPositionsTabProperties(tabType));
    closeAddTabPopover();
  });

  const { renderAddTab, close: closeAddTabPopover } = useAddTabPopover({
    options: tabTypeOptions,
    getLabel: getPositionsTabTypeLabel,
    size: TabSize.Large,
    onSelection: handleNewTabTypeSelection,
    appearance: TabAppearance.Underlined,
  });

  const { setPortalRef: filtersContainerRef } = usePortal(BLOTTER_TABLE_FILTERS_CONTAINER_ID);

  return (
    <>
      {tabTypeOptions.includes('Unified') && <Tour tour={TourName.v130MarketAccountsTab} />}
      <Tabs {...tabs} h="100%" size={TabSize.Large}>
        <TabList
          renderAddTab={renderAddTab}
          isBordered
          rightItems={
            <HStack gap="spacing12">
              <Box ref={filtersContainerRef} />
            </HStack>
          }
          data-testid="positions-blotter-tablist"
        >
          {tabs.items.map(tab => (
            <Tab
              key={tab.id}
              {...tab}
              {...(tab.tabType === 'Unified' ? { 'data-testid': POSITIONS_MARKET_ACCOUNTS_TAB_TEST_ID } : {})}
            />
          ))}
        </TabList>
        <TabPanels style={{ flex: '1', display: 'flex', flexDirection: 'column' }}>
          {tabs.items.length === 0 ? (
            <EmptyView flex={1} p="spacing16">
              <Box textAlign="center">
                Contact Talos Support to enable Derivative or Market Accounts position views.
                <br />A Sub Account position breakdown will appear once Sub Accounts are configured.
              </Box>
            </EmptyView>
          ) : (
            tabs.items.map(tab => (
              <AccordionGroup key={tab.id}>
                {renderPositionsTab(tab, {
                  initialFiltersOpen,
                  onUpdateTab: handleUpdateTab,
                  onCloneTab: handleCloneTab,
                })}
              </AccordionGroup>
            ))
          )}
        </TabPanels>
      </Tabs>
    </>
  );
}
